import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Paper,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Folder as FolderIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { supabase } from '../../services/supabase';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useNavigate } from 'react-router-dom';

interface ContentProject {
  id: string;
  project_name: string;
  primary_keyword: string;
  status: 'research' | 'writing' | 'published';
  created_at: string;
  updated_at: string;
  notes: string;
  target_url: string;
  published_url_id?: string;
  archived: boolean;
}

export const ContentProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ContentProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({
    project_name: '',
    primary_keyword: '',
    notes: '',
    target_url: ''
  });
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<ContentProject | null>(null);
  
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);

  useEffect(() => {
    if (selectedWebsite?.website_id) {
      fetchProjects();
    } else {
      setLoading(false);
    }
  }, [selectedWebsite, tabValue]);

  const fetchProjects = async () => {
    if (!selectedWebsite?.website_id) return;
    
    setLoading(true);
    try {
      let query = (supabase as any)
        .from('content_projects')
        .select('*')
        .eq('website_id', selectedWebsite.website_id)
        .order('created_at', { ascending: false });

      if (tabValue === 0) {
        query = query.eq('archived', false);
      } else if (tabValue === 2) {
        query = query.eq('archived', true);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    if (!selectedWebsite?.website_id || !newProject.project_name) return;

    try {
      const { error } = await (supabase as any)
        .from('content_projects')
        .insert([{
          website_id: selectedWebsite.website_id,
          ...newProject
        }]);
      
      if (error) throw error;
      
      setShowCreateModal(false);
      setNewProject({ project_name: '', primary_keyword: '', notes: '', target_url: '' });
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const updateProjectStatus = async (projectId: string, status: string) => {
    try {
      const { error } = await (supabase as any)
        .from('content_projects')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', projectId);
      
      if (error) throw error;
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const archiveProject = async (project: ContentProject) => {
    try {
      const { error } = await (supabase as any)
        .from('content_projects')
        .update({ archived: !project.archived, updated_at: new Date().toISOString() })
        .eq('id', project.id);
      
      if (error) throw error;
      fetchProjects();
      handleCloseMenu();
    } catch (error) {
      console.error('Error archiving project:', error);
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, project: ContentProject) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'research': return 'warning';
      case 'writing': return 'info';
      case 'published': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (!selectedWebsite) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          Please select a website from the dropdown above to view content projects.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Content Projects
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{ borderRadius: 2 }}
            >
              New Project
            </Button>
          </Box>

          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              textColor="primary"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              <Tab label="Active Projects" />
              <Tab label="All Projects" />
              <Tab label="Archived" />
            </Tabs>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : projects.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <FolderIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No projects found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {tabValue === 0 ? 'Create your first content project to get started' : 
                 tabValue === 2 ? 'No archived projects' : 'No projects yet'}
              </Typography>
              {tabValue === 0 && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Project
                </Button>
              )}
            </Card>
          ) : (
            <Grid container spacing={3}>
              {projects.map((project) => (
                <Grid item xs={12} md={6} lg={4} key={project.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'box-shadow 0.3s',
                      '&:hover': {
                        boxShadow: 4,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <Box 
                      sx={{ p: 3, flexGrow: 1 }}
                      onClick={() => navigate(`/content-projects/${project.id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {project.project_name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenMenu(e, project);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                      
                      {project.primary_keyword && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Primary Keyword:</strong> {project.primary_keyword}
                        </Typography>
                      )}
                      
                      {project.target_url && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Target URL:</strong> {project.target_url}
                        </Typography>
                      )}
                      
                      {project.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {project.notes.length > 100 ? project.notes.substring(0, 100) + '...' : project.notes}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={getStatusLabel(project.status)}
                        color={getStatusColor(project.status) as any}
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        Created {new Date(project.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Create Project Dialog */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Content Project</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={newProject.project_name}
              onChange={(e) => setNewProject({...newProject, project_name: e.target.value})}
              placeholder="Ultimate Guide to SEO..."
              sx={{ mb: 3 }}
              required
            />
            <TextField
              fullWidth
              label="Primary Keyword"
              value={newProject.primary_keyword}
              onChange={(e) => setNewProject({...newProject, primary_keyword: e.target.value})}
              placeholder="seo best practices"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Target URL (planned)"
              value={newProject.target_url}
              onChange={(e) => setNewProject({...newProject, target_url: e.target.value})}
              placeholder="/blog/ultimate-seo-guide"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Notes"
              value={newProject.notes}
              onChange={(e) => setNewProject({...newProject, notes: e.target.value})}
              placeholder="Research notes, content ideas..."
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button 
            onClick={createProject} 
            variant="contained" 
            disabled={!newProject.project_name}
          >
            Create Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          if (selectedProject) {
            navigate(`/content-projects/${selectedProject.id}`);
          }
          handleCloseMenu();
        }}>
          <LaunchIcon sx={{ mr: 1, fontSize: 20 }} /> Open Research Hub
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedProject) {
            archiveProject(selectedProject);
          }
        }}>
          {selectedProject?.archived ? (
            <>
              <UnarchiveIcon sx={{ mr: 1, fontSize: 20 }} /> Unarchive
            </>
          ) : (
            <>
              <ArchiveIcon sx={{ mr: 1, fontSize: 20 }} /> Archive
            </>
          )}
        </MenuItem>
      </Menu>
    </Container>
  );
};